import type { PortalConfig } from './config';
import type { AttendancePunch } from './types';

const ICON_IN =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
const ICON_OUT =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>';

const ICON_REJECTED =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/></svg>';
const ICON_NOTE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Which way the crossing went. The one place besides connection state where colour is spent. */
export type PunchDirection = 'in' | 'out';

/**
 * The kiosk stage: one punch at a time, held for a configurable window, then the idle view.
 *
 * A single card rather than a feed because of where this runs — someone walks up, taps, and needs to
 * confirm at a glance that *they* were recorded. A scrolling list of colleagues answers a different
 * question and leaks more than it needs to.
 */
export class PunchBoard {
  readonly element: HTMLElement;

  private hideTimer: number | undefined;
  private idleTimer: number | undefined;

  constructor(private readonly config: PortalConfig) {
    this.element = document.createElement('main');
    this.element.className = 'stage';
    this.renderIdle();
  }

  /**
   * Shows a punch and restarts the hold timer. A new punch always wins immediately — at a shift
   * change people queue, and making someone wait out the previous person's timer would be absurd.
   */
  show(punch: AttendancePunch): void {
    this.stopIdleClock();

    const direction = PunchBoard.direction(punch.slotFilled);
    const [time, meridiem] = this.formatTime(punch.punchDateTime);

    const article = document.createElement('article');
    article.className = 'punch';
    article.innerHTML = `
      <div class="punch-photo"></div>
      <div class="punch-body">
        <div class="greeting"></div>
        <h1 class="punch-name"></h1>
        <p class="punch-position" hidden></p>

        <div class="punch-mark">
          <span class="slot-badge" data-dir="${direction}">
            ${direction === 'in' ? ICON_IN : ICON_OUT}
            ${direction === 'in' ? 'Time In' : 'Time Out'}
          </span>
          <span class="punch-time"></span>
        </div>

        <dl class="punch-facts">
          <div class="fact" data-fact="employeeId">
            <dt class="fact-label">Employee ID</dt>
            <dd class="fact-value"></dd>
          </div>
          <div class="fact" data-fact="recordDate">
            <dt class="fact-label">Attendance Date</dt>
            <dd class="fact-value"></dd>
          </div>
          <div class="fact" data-fact="slot">
            <dt class="fact-label">Recorded As</dt>
            <dd class="fact-value"></dd>
          </div>
        </dl>
        <div class="punch-note" hidden>
          <div class="punch-note-icon"></div>
          <div class="punch-note-body">
            <div class="punch-note-title" hidden></div>
            <div class="punch-note-lines"></div>
          </div>
        </div>
      </div>
    `;

    const name = punch.employeeName?.trim() || punch.personId;

    article.querySelector('.greeting')!.textContent = PunchBoard.greeting(punch.punchDateTime);
    article.querySelector('.punch-name')!.textContent = name;

    const position = article.querySelector<HTMLElement>('.punch-position')!;
    position.textContent = punch.position ?? '';
    position.hidden = !punch.position;

    // Built as nodes rather than interpolated, so a name or remark containing markup cannot reach
    // innerHTML — the payload is server-resolved, but a wall display is not the place to rely on it.
    const punchTime = article.querySelector('.punch-time')!;
    const meridiemNode = document.createElement('span');
    meridiemNode.className = 'mer';
    meridiemNode.textContent = meridiem;
    punchTime.replaceChildren(document.createTextNode(time), meridiemNode);

    setFact(article, 'employeeId', punch.personId);
    setFact(article, 'recordDate', this.formatDate(punch.recordDate));
    setFact(article, 'slot', this.formatSlot(punch.slotFilled));

    this.renderNote(article.querySelector<HTMLElement>('.punch-note')!, punch);

    // A refused punch keeps the card — the person needs to see who was refused — but not the parts
    // that assert it happened. `direction` defaults to "in" for an absent slot, so a rejection was
    // otherwise crowned with a green "Time In" badge above an empty time: the single most misleading
    // thing the board could show someone who is already walking away.
    if (punch.isSuccess === false) {
      article.querySelector<HTMLElement>('.punch-mark')!.hidden = true;
    }

    this.renderPhoto(article.querySelector('.punch-photo')!, punch, name);

    this.element.replaceChildren(article);

    // Restart rather than accumulate — otherwise a burst of punches would pile up timers and the
    // last card could vanish early when an older one fires.
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => this.renderIdle(), this.config.displayMs);
  }

  /**
   * The note at the foot of the card — the same slot the plain remarks paragraph used to occupy,
   * restyled as the Angular app's clock result alert: a toned icon tile beside a title and body, so
   * an admin who has seen one recognises the other.
   *
   * Carries both kinds of message that land here. An ordinary remark on a punch that was recorded
   * ("Late") stays advisory; a rejection is toned as an error, because the person is walking away
   * from a terminal believing they clocked in.
   */
  private renderNote(host: HTMLElement, punch: AttendancePunch): void {
    const [title, body] = PunchBoard.splitMessages(punch);

    if (!title && body.length === 0) {
      host.hidden = true;
      return;
    }

    // `isSuccess === false` rather than `!isSuccess`: a message from an older HRS build omits the
    // field entirely, and those only ever described a punch that succeeded.
    const rejected = punch.isSuccess === false;

    host.hidden = false;
    host.dataset.tone = rejected ? 'error' : 'note';
    host.setAttribute('role', rejected ? 'alert' : 'status');
    host.querySelector('.punch-note-icon')!.innerHTML = rejected ? ICON_REJECTED : ICON_NOTE;

    const titleNode = host.querySelector<HTMLElement>('.punch-note-title')!;
    titleNode.textContent = title;
    titleNode.hidden = !title;

    // Built as nodes, not interpolated: the rejection text now carries a caller-supplied IP address,
    // so it must never reach innerHTML.
    host.querySelector('.punch-note-lines')!.replaceChildren(
      ...body.map((line) => {
        const p = document.createElement('p');
        p.className = 'punch-note-text';
        p.textContent = line;
        return p;
      })
    );
  }

  /**
   * The house convention, shared with the Angular toasts and the clock result alert: one message is
   * body text with no title; with several, the first is the title and the rest are the body.
   *
   * Falls back to the joined `remarks` string when `messages` is absent — both for an ordinary remark
   * on a successful punch, which never populates the array, and for a message from an HRS build
   * predating it.
   */
  static splitMessages(punch: AttendancePunch): [title: string, body: string[]] {
    const messages = punch.messages?.filter((m) => m?.trim()) ?? [];

    if (messages.length === 0) {
      const remarks = punch.remarks?.trim();
      return ['', remarks ? [remarks] : []];
    }

    if (messages.length === 1) return ['', [messages[0]!]];

    return [messages[0]!, messages.slice(1)];
  }

  /**
   * The stage between punches.
   *
   * Not a blank screen: on a wall an empty board is indistinguishable from a crashed browser, and
   * the composed emptiness is the point — a surface at rest should look intentional, the way a
   * well-set table looks before anyone sits.
   */
  private renderIdle(): void {
    const section = document.createElement('section');
    section.className = 'idle';
    section.innerHTML = `
      <div class="idle-clock"><span class="idle-time"></span><span class="mer idle-mer"></span></div>
      <div class="idle-date"></div>
      <div class="idle-rule"></div>
      <div class="idle-hint">Present your badge or finger to record attendance</div>
    `;

    this.element.replaceChildren(section);

    this.tickIdleClock();
    this.stopIdleClock();
    this.idleTimer = window.setInterval(() => this.tickIdleClock(), 1_000);
  }

  private tickIdleClock(): void {
    const time = this.element.querySelector('.idle-time');
    if (!time) return;

    const now = new Date();
    const hours = now.getHours();
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;

    time.textContent = `${hour12}:${String(now.getMinutes()).padStart(2, '0')}`;
    this.element.querySelector('.idle-mer')!.textContent = hours >= 12 ? 'PM' : 'AM';
    this.element.querySelector('.idle-date')!.textContent = now.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private stopIdleClock(): void {
    window.clearInterval(this.idleTimer);
    this.idleTimer = undefined;
  }

  private renderPhoto(host: HTMLElement, punch: AttendancePunch, name: string): void {
    const url = this.profilePictureUrl(punch);

    if (!url) {
      host.replaceChildren(initialsNode(name));
      return;
    }

    const image = document.createElement('img');
    image.src = url;
    image.alt = '';
    // A broken image on a wall display looks like a fault; fall back to initials instead.
    image.addEventListener('error', () => host.replaceChildren(initialsNode(name)));
    host.replaceChildren(image);
  }

  /** Mirrors the Angular app's `profilePictureUrl` helper — photos are served tenant-scoped by STS. */
  private profilePictureUrl(punch: AttendancePunch): string | null {
    return punch.profilePicture
      ? `${this.config.stsApiUrl}/files/pp/${punch.tenantId ?? 0}/${punch.profilePicture}`
      : null;
  }

  /**
   * Which way the crossing went, from the slot's trailing `In`/`Out` ("FirstHalfIn").
   *
   * Defaults to `in` for an absent or unrecognised slot: the badge is a summary of the literal slot
   * shown a few lines below it, so a wrong guess is corrected on the same card, whereas omitting the
   * badge entirely would leave a hole in the composition.
   */
  static direction(slot: string | null): PunchDirection {
    return slot?.trim().toLowerCase().endsWith('out') ? 'out' : 'in';
  }

  /**
   * Derived from the punch hour, not the browser clock.
   *
   * A punch carries site-local wall time, so a display in another zone — or one still showing a
   * punch from a few minutes before midnight — greets the person by the moment they actually
   * crossed, not by where the browser happens to be.
   */
  static greeting(punchDateTime: string | null): string {
    const hour = Number(/T(\d{2}):/.exec(punchDateTime ?? '')?.[1]);
    if (!Number.isFinite(hour)) return 'Welcome';
    if (hour < 12) return 'Good morning';
    return hour < 18 ? 'Good afternoon' : 'Good evening';
  }

  /**
   * Punch times are site-local wall clock, not instants, so they are formatted by slicing the string.
   * Constructing a `Date` would have the browser apply its own zone offset and show the wrong time.
   */
  private formatTime(value: string | null): [time: string, meridiem: string] {
    // Renders as nothing rather than as a dash: the punch time is the largest element on the card,
    // and a lone "—" at that size reads as a fault. The slot badge beside it still says what
    // happened. A present-but-unparseable value falls through to the raw string below, which is
    // deliberate — that one is worth seeing, because it means the device sent something unexpected.
    if (!value) return ['', ''];

    const match = /T(\d{2}):(\d{2})/.exec(value);
    if (!match) return [value, ''];

    const hours = Number(match[1]);
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return [`${hour12}:${match[2]}`, hours >= 12 ? 'PM' : 'AM'];
  }

  private formatDate(value: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return value;

    return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}`;
  }

  /** Server slot names are PascalCase ("FirstHalfIn"); split them for a human reading a wall. */
  private formatSlot(slot: string | null): string {
    return slot ? slot.replace(/([a-z])([A-Z])/g, '$1 $2') : '';
  }
}

function setFact(root: ParentNode, key: string, value: string): void {
  root.querySelector(`[data-fact="${key}"] .fact-value`)!.textContent = value || '—';
}

function initialsNode(name: string): HTMLElement {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

  const span = document.createElement('span');
  span.className = 'initials';
  span.textContent = initials || '?';
  return span;
}
